/**
 * The route table — and, with it, the rail's mode on every surface.
 *
 * The mode lives on the route's `handle` rather than inside the component, so the shell
 * knows what the rail is *before* the surface renders. That is what stops the rail flashing
 * the previous mode on a navigation, and it is what makes "which mode does this surface get"
 * a thing you can read in one file (PRD 04 §6's table, in code).
 *
 * **Every surface is its own chunk.** `React.lazy` per route, so the first paint carries the
 * shell and the surface you asked for and nothing else; the kit — which imports every
 * primitive at once and is not part of the product — is the biggest of them and now costs a
 * non-admin nothing. `surface()` supplies the wait in the house voice.
 *
 * The Stories area (`studio/08`): the **story page** at `/stories/:id` (open at beat 0) and
 * `/stories/:id/beats/:i`, on the source ground; **Formats** at `/stories/:id/formats` and
 * `/formats/:mediumId`, and **Make** at `/formats/:mediumId/make`, on the working ground; the
 * composer in format mode at `/formats/:mediumId/scenes/:j`, on the source ground. The old
 * `/compose/:i`, `/preview` and `/make` addresses redirect.
 */

import type { RouteObject } from 'react-router';
import { Navigate } from 'react-router';

import { LazyAppShell, RequireAuth } from '../auth/RequireAuth.js';
import { SignIn } from '../auth/SignIn.js';
import {
  RAIL_NONE,
  RAIL_RECEDE,
  RAIL_SHELF,
  RAIL_SIBLINGS,
  RAIL_SOURCE,
  RAIL_STATE,
} from '../shell/railModes.js';
import type { RailHandle } from '../shell/railModes.js';
import { lazySurface, surface } from './Surface.js';
import { NotFound, OldCompose, OldMake, OldPreview, StoryIntake } from './surfaces.js';

const Kit = lazySurface(async () => ({
  default: (await import('../kit/Kit.js')).Kit,
}));
const LibraryHome = lazySurface(async () => ({
  default: (await import('../library/LibraryHome.js')).LibraryHome,
}));
const PieceDetail = lazySurface(async () => ({
  default: (await import('../library/PieceDetail.js')).PieceDetail,
}));
const MeetCharacter = lazySurface(async () => ({
  default: (await import('../library/MeetCharacter.js')).MeetCharacter,
}));
const StoriesHome = lazySurface(async () => ({
  default: (await import('../stories/StoriesHome.js')).StoriesHome,
}));
const StoryPage = lazySurface(async () => ({
  default: (await import('../stories/StoryPage.js')).StoryPage,
}));
const Formats = lazySurface(async () => ({
  default: (await import('../formats/Formats.js')).Formats,
}));
const FormatComposer = lazySurface(async () => ({
  default: (await import('../formats/FormatComposer.js')).FormatComposer,
}));
const MakeFormat = lazySurface(async () => ({
  default: (await import('../formats/MakeFormat.js')).MakeFormat,
}));
const Browse = lazySurface(async () => ({
  default: (await import('../browse/Browse.js')).Browse,
}));

const handle = (rail: RailHandle['rail']): RailHandle => ({ rail });

export const routes: RouteObject[] = [
  // The kit is outside the shell and outside auth: it is about the parts, not the room.
  { path: '/_kit', element: surface(Kit) },
  { path: '/signin', element: <SignIn /> },

  {
    path: '/',
    element: <RequireAuth />,
    children: [
      { index: true, element: <Navigate to="/library" replace /> },

      // Library — build the world.
      { path: 'library', element: surface(LibraryHome), handle: handle(RAIL_SHELF) },
      { path: 'library/:kind/:id', element: surface(PieceDetail), handle: handle(RAIL_SIBLINGS) },
      {
        path: 'library/:kind/:id/meet',
        element: surface(MeetCharacter),
        handle: handle(RAIL_RECEDE),
      },

      // Stories — the story page, then its formats.
      { path: 'stories', element: surface(StoriesHome), handle: handle(RAIL_STATE) },
      { path: 'stories/:id', element: surface(StoryPage), handle: handle(RAIL_SOURCE) },
      {
        path: 'stories/:id/beats/:beatIndex',
        element: surface(StoryPage),
        handle: handle(RAIL_SOURCE),
      },
      { path: 'stories/:id/formats', element: surface(Formats), handle: handle(RAIL_SIBLINGS) },
      {
        path: 'stories/:id/formats/:mediumId',
        element: surface(Formats),
        handle: handle(RAIL_SIBLINGS),
      },
      {
        path: 'stories/:id/formats/:mediumId/scenes/:sceneIndex',
        element: surface(FormatComposer),
        handle: handle(RAIL_SOURCE),
      },
      {
        path: 'stories/:id/formats/:mediumId/make',
        element: surface(MakeFormat),
        handle: handle(RAIL_SIBLINGS),
      },

      // The addresses the Stories area had before `studio/08`. Old links keep working.
      { path: 'stories/:id/compose/:sceneIndex', element: <OldCompose /> },
      { path: 'stories/:id/preview', element: <OldPreview /> },
      { path: 'stories/:id/make', element: <OldMake /> },

      // The two doors.
      { path: 'browse', element: surface(Browse), handle: handle(RAIL_NONE) },
      { path: 'intake', element: <StoryIntake />, handle: handle(RAIL_RECEDE) },

      { path: '*', element: <NotFound />, handle: handle(RAIL_SHELF) },
    ],
  },
];

/**
 * Warm every surface — **and the shell**, which is lazy too since `studio/11` (it is the room
 * Firestore lives in, and the sign-in page must not download it).
 *
 * The app never calls this — splitting the surfaces is the point. The tests do, once, so a
 * route mounts synchronously and they exercise the real table rather than a second copy of
 * it that could drift from it.
 */
export async function preloadSurfaces(): Promise<void> {
  await Promise.all(
    [
      LazyAppShell,
      Kit,
      LibraryHome,
      PieceDetail,
      MeetCharacter,
      StoriesHome,
      StoryPage,
      Formats,
      FormatComposer,
      MakeFormat,
      Browse,
    ].map((surfaceComponent) => surfaceComponent.preload()),
  );
}
